import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import MessageSnackbar from "@/__core/helpers/components/MessageSnackbar";
import {
  useTaskManagement,
} from "../hooks/useTasks";
import {
  CreateTaskRequest,
  AssignTaskRequest,
  UpdateTaskRequest,
  Task,
} from "../models/TaskModel";
import { createTaskAsync, updateTaskAsync } from "../redux/TaskRedux";
import NewTaskModal from "./NewTaskModal";
import ListTasksOrder from "./ListTasksOrder";

// Component-specific interfaces
interface TaskManagementProps {
  orderId?: string;
  contextCompanyId?: string;
}

const TaskManagement: React.FC<TaskManagementProps> = ({ orderId, contextCompanyId }) => {
  const dispatch = useDispatch();
  const {
    tasks,
    loading,
    taskStatuses,
    taskPriorities,
    users,
    groups,
    initializeOrderTasks,
    assignTask,
    // updateTask,
    changeTaskStatus,
    getUserDisplayName,
  } = useTaskManagement(orderId, contextCompanyId);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (orderId) {
      initializeOrderTasks();
    }
  }, [orderId, initializeOrderTasks]);

  // Load data when modal opens (only once)
  useEffect(() => {
    if (createDialogOpen) {
      // Load all necessary data when create dialog opens
      initializeOrderTasks();
    }
  }, [createDialogOpen, initializeOrderTasks]);

  const handleCreateTask = async (taskData: CreateTaskRequest): Promise<Task> => {
    if (!orderId) {
      const message = "Không thể tạo task: chưa có ID đơn hàng";
      setErrorMessage(message);
      throw new Error(message);
    }

    try {
      // Ensure only one assignment is sent (user takes priority over group)
      const { assigned_to_user_id, assigned_to_group_id, ...restTaskData } = taskData;
      
      const taskDataWithOrderId = {
        ...restTaskData,
        object_id: orderId,
        // Ensure only one assignment is sent
        // Send null to explicitly clear the other assignment
        assigned_to_user_id: assigned_to_user_id || null,
        assigned_to_group_id: assigned_to_user_id ? null : (assigned_to_group_id || null),
      };
      
      console.log("Create task data:", taskDataWithOrderId);
      const createdTask = await (dispatch(createTaskAsync(taskDataWithOrderId)) as any).unwrap();
      // Refresh to get list serializer fields used by ListTasksOrder (status_name, assignee names, counts)
      initializeOrderTasks();
      setSuccessMessage("Tạo nhiệm vụ thành công");
      return createdTask as Task;
    } catch (err: any) {
      const message = err?.message || "Không thể tạo task";
      setErrorMessage(message);
      throw err;
    }
  };

  const handleAssignTask = async (taskId: string, assignData: AssignTaskRequest) => {
    try {
      await assignTask(taskId, assignData);
    } catch (err: any) {
      setErrorMessage(err.message || "Không thể gán task");
    }
  };

  const handleEditTask = async (taskId: string, updateData: UpdateTaskRequest): Promise<Task> => {
    try {
      console.log("Update task request:", { id: taskId, taskData: updateData });
      const updatedTask = await (dispatch(updateTaskAsync({ id: taskId, taskData: updateData })) as any).unwrap();
      // Refresh tasks after update
      initializeOrderTasks();
      setSuccessMessage("Cập nhật nhiệm vụ thành công");
      return updatedTask as Task;
    } catch (err: any) {
      console.error("Update task error:", err);
      const message = err?.message || "Không thể cập nhật task";
      setErrorMessage(message);
      throw err;
    }
  };

  const handleDeleteTask = (taskId: string) => {
    // TODO: Implement delete functionality
    console.log("Delete task:", taskId);
  };

  const handleStatusChange = (taskId: string, statusId: string) => {
    try {
      changeTaskStatus(taskId, { status_id: statusId });
    } catch (err: any) {
      setErrorMessage(err.message || "Không thể thay đổi trạng thái");
    }
  };

  const handleTaskFiltersChange = useCallback((filters: { assigned_by?: string; assigned_to_group?: string }) => {
    initializeOrderTasks(filters);
  }, [initializeOrderTasks]);

  // Show message when no orderId (creating new order)
  if (!orderId) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
          Quản lý nhiệm vụ
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Lưu đơn hàng để có thể tạo và quản lý nhiệm vụ
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
      <ListTasksOrder
        tasks={tasks}
        loading={loading}
        taskStatuses={taskStatuses}
        taskPriorities={taskPriorities}
        users={users}
        groups={groups}
        contextCompanyId={contextCompanyId}
        orderId={orderId}
        getUserDisplayName={getUserDisplayName}
        onCreateTask={() => setCreateDialogOpen(true)}
        onAssignTask={handleAssignTask}
        onEditTask={handleEditTask}
        onDeleteTask={handleDeleteTask}
        onStatusChange={handleStatusChange}
        onFiltersChange={handleTaskFiltersChange}
      />

      <NewTaskModal
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateTask}
        taskStatuses={taskStatuses}
        taskPriorities={taskPriorities}
        users={users}
        companyId={contextCompanyId}
        getUserDisplayName={getUserDisplayName}
        orderId={orderId}
      />

      <MessageSnackbar
        open={!!errorMessage}
        message={errorMessage}
        severity='error'
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />

      <MessageSnackbar
        open={!!successMessage}
        message={successMessage}
        severity='success'
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default TaskManagement;
